import { faDownload, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Stack } from "@mui/material";
import { useMemo } from "react";
import { FormProvider, Resolver, useForm, useWatch } from "react-hook-form";
import {
  useEventCallback,
  useLocalStorage,
  useReadLocalStorage,
} from "usehooks-ts";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  Icon,
} from "@/components/ui-v2";
import { useSnackbar } from "@/components/ui-v2/Snackbar";
import { useBasicData } from "@/hooks";
import { useExportObjects } from "@/hooks/useExportObjects";
import useOnValueChanged from "@/hooks/useOnValueChanged";

import { SelectedObject } from "../selection-sidebar/types";
import {
  EXPORT_DIALOG_FORM_DEFAULT_VALUES,
  EXPORT_DIALOG_VIDEO_FORM_DEFAULT_VALUES,
  EXPORT_FORM_FIELDS,
  ExportForm,
  ExportFormState,
  ExportModality,
  getValidationSchema,
} from "./components/ExportForm";
import { ExportFormUnitsContainer } from "./components/ExportFormUnitsContainer";
import { ExportLoadingBanner } from "./components/ExportLoadingBanner";
import { ExportPreview } from "./components/ExportPreview";
import { Dimensions } from "./types";

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  objects: SelectedObject[];
}

type ExportFormStateLocalStorage = Pick<
  ExportFormState,
  "format" | "removeBackground" | "includeMetadata" | "exportSize"
>;

type ExportVideoFormStateLocalStorage = Pick<
  ExportFormState,
  | "format"
  | "fps"
  | "frameExtractionFps"
  | "videoCodec"
  | "compression"
  | "includeMetadata"
>;

const getFormDefaultValues = (
  savedOptions: ExportFormStateLocalStorage | undefined,
  savedVideoOptions: ExportVideoFormStateLocalStorage | undefined,
  dimensions: Dimensions,
  hasSavedSelections: boolean,
  hasSavedVideoSelections: boolean,
  modality: ExportModality,
  alwaysIncludeMetadata: boolean,
): Partial<ExportFormState> => {
  if (modality === "video") {
    return {
      ...EXPORT_DIALOG_VIDEO_FORM_DEFAULT_VALUES,
      ...(hasSavedVideoSelections && savedVideoOptions
        ? {
            [EXPORT_FORM_FIELDS.FORMAT]: savedVideoOptions.format,
            [EXPORT_FORM_FIELDS.FPS]: savedVideoOptions.fps,
            [EXPORT_FORM_FIELDS.FRAME_EXTRACTION_FPS]:
              savedVideoOptions.frameExtractionFps,
            [EXPORT_FORM_FIELDS.VIDEO_CODEC]: savedVideoOptions.videoCodec,
            [EXPORT_FORM_FIELDS.COMPRESSION]: savedVideoOptions.compression,
            [EXPORT_FORM_FIELDS.INCLUDE_METADATA]:
              savedVideoOptions.includeMetadata,
            [EXPORT_FORM_FIELDS.SAVE_SELECTIONS]: true,
          }
        : {}),
      // Overrides a saved preference too: the workspace's requirement is not the
      // user's to remember off.
      ...(alwaysIncludeMetadata
        ? { [EXPORT_FORM_FIELDS.INCLUDE_METADATA]: true }
        : {}),
      [EXPORT_FORM_FIELDS.ORIGINAL_HEIGHT]: dimensions.height,
      [EXPORT_FORM_FIELDS.ORIGINAL_WIDTH]: dimensions.width,
    };
  }

  const savedSize = hasSavedSelections ? savedOptions?.exportSize ?? 1 : 1;

  return {
    ...EXPORT_DIALOG_FORM_DEFAULT_VALUES,
    ...(hasSavedSelections && savedOptions
      ? {
          [EXPORT_FORM_FIELDS.FORMAT]: savedOptions.format,
          [EXPORT_FORM_FIELDS.INCLUDE_METADATA]: savedOptions.includeMetadata,
          [EXPORT_FORM_FIELDS.REMOVE_BACKGROUND]: savedOptions.removeBackground,
          [EXPORT_FORM_FIELDS.SAVE_SELECTIONS]: true,
          [EXPORT_FORM_FIELDS.EXPORT_SIZE]: savedSize,
        }
      : {}),
    ...(alwaysIncludeMetadata
      ? { [EXPORT_FORM_FIELDS.INCLUDE_METADATA]: true }
      : {}),
    [EXPORT_FORM_FIELDS.EXPORT_HEIGHT]: dimensions.height
      ? Math.round(dimensions.height * savedSize)
      : undefined,
    [EXPORT_FORM_FIELDS.EXPORT_WIDTH]: dimensions.width
      ? Math.round(dimensions.width * savedSize)
      : undefined,
    [EXPORT_FORM_FIELDS.ORIGINAL_ASPECT_RATIO]:
      dimensions.width && dimensions.height
        ? dimensions.width / dimensions.height
        : undefined,
    [EXPORT_FORM_FIELDS.ORIGINAL_HEIGHT]: dimensions.height,
    [EXPORT_FORM_FIELDS.ORIGINAL_WIDTH]: dimensions.width,
  };
};

const getImageDimensions = (objects: SelectedObject[]): Dimensions => {
  if (objects.length === 0) {
    return { width: undefined, height: undefined };
  }

  // Get dimensions from all File objects
  const dimensions = objects
    .filter(
      (obj): obj is Extract<SelectedObject, { __typename: "File" }> =>
        obj.__typename === "File",
    )
    .map((obj) => {
      const { width, height } = obj.metadata ?? {};
      return width && height ? { width, height } : null;
    })
    .filter((d): d is { width: number; height: number } => d !== null);

  // If not all objects have dimensions, don't show the size form
  if (dimensions.length !== objects.length) {
    return { width: undefined, height: undefined };
  }

  // Check if all dimensions are the same
  const firstDimension = dimensions[0];
  const allSameDimensions = dimensions.every(
    (d) =>
      d.width === firstDimension.width && d.height === firstDimension.height,
  );

  if (allSameDimensions) {
    return firstDimension;
  }

  return { width: undefined, height: undefined };
};

export const ExportDialog = (props: ExportDialogProps) => {
  const { isOpen, onClose, objects } = props;

  const { cancelExport, exportObjects, getExportModality, isLoading } =
    useExportObjects();

  const exportModality = useMemo(
    () => getExportModality(objects),
    [getExportModality, objects],
  );

  const validationSchema = useMemo(
    () => getValidationSchema(exportModality),
    [exportModality],
  );

  const imageDimensions = useMemo(() => getImageDimensions(objects), [objects]);

  const LOCAL_STORAGE_KEY = "export-dialog-2d-options";
  const VIDEO_LOCAL_STORAGE_KEY = "export-dialog-video-options";

  const { workspace } = useBasicData();
  const alwaysIncludeMetadata = !!workspace?.alwaysIncludeMetadata;

  const persistedValue = useReadLocalStorage(LOCAL_STORAGE_KEY);
  const hasSavedSelections = persistedValue !== null;

  const persistedVideoValue = useReadLocalStorage(VIDEO_LOCAL_STORAGE_KEY);
  const hasSavedVideoSelections = persistedVideoValue !== null;

  const [savedExport2dOptions, setSavedExport2dOptions] = useLocalStorage<
    ExportFormStateLocalStorage | undefined
  >(LOCAL_STORAGE_KEY, undefined);

  const [savedVideoOptions, setSavedVideoOptions] = useLocalStorage<
    ExportVideoFormStateLocalStorage | undefined
  >(VIDEO_LOCAL_STORAGE_KEY, undefined);

  const form = useForm<ExportFormState>({
    resolver: yupResolver(validationSchema) as Resolver<ExportFormState>,
    mode: "onChange",
    defaultValues: getFormDefaultValues(
      savedExport2dOptions,
      savedVideoOptions,
      imageDimensions,
      hasSavedSelections,
      hasSavedVideoSelections,
      exportModality,
      alwaysIncludeMetadata,
    ),
  });

  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
    setValue,
  } = form;

  const formValues = useWatch({
    control,
  }) as Partial<ExportFormState>;

  const { showError } = useSnackbar();

  const shouldShowLoadingBanner =
    isLoading && (objects.length > 2 || exportModality === "video");
  const isMultiple = objects.length > 1;
  const title = isMultiple
    ? `Download ${objects.length} Assets`
    : "Download Asset";
  const downloadButtonText = isMultiple ? "Download All" : "Download";

  const initializeForm = useEventCallback(() => {
    reset(
      getFormDefaultValues(
        savedExport2dOptions,
        savedVideoOptions,
        imageDimensions,
        hasSavedSelections,
        hasSavedVideoSelections,
        exportModality,
        alwaysIncludeMetadata,
      ),
    );
  });

  useOnValueChanged(isOpen, (_oldValue, newValue) => {
    if (newValue) {
      initializeForm();
    }
  });

  useOnValueChanged(exportModality, () => {
    initializeForm();
  });

  // `useBasicData` reads cache-only, so the workspace can land after the dialog
  // opened and the form defaults were taken. Carry the requirement across when it
  // does, or the control reads unticked for a workspace that embeds regardless.
  useOnValueChanged(alwaysIncludeMetadata, (_oldValue, newValue) => {
    if (newValue) {
      setValue(EXPORT_FORM_FIELDS.INCLUDE_METADATA, true);
    }
  });

  const handleCancel = useEventCallback(() => {
    cancelExport();
    onClose();
  });

  const handleDownload = useEventCallback(async (data: ExportFormState) => {
    try {
      await exportObjects(objects, data);
      onClose();
    } catch (error) {
      console.error("Error downloading files:", error);
      showError("Failed to download files. Please try again.");
    }
  });

  const handleFormValuesChanged = useEventCallback(
    (prevFormValues: ExportFormState, newFormValues: ExportFormState) => {
      if (newFormValues.saveSelections) {
        if (exportModality === "video") {
          setSavedVideoOptions({
            [EXPORT_FORM_FIELDS.FORMAT]: newFormValues.format,
            [EXPORT_FORM_FIELDS.FPS]: newFormValues.fps,
            [EXPORT_FORM_FIELDS.FRAME_EXTRACTION_FPS]:
              newFormValues.frameExtractionFps,
            [EXPORT_FORM_FIELDS.VIDEO_CODEC]: newFormValues.videoCodec,
            [EXPORT_FORM_FIELDS.COMPRESSION]: newFormValues.compression,
            [EXPORT_FORM_FIELDS.INCLUDE_METADATA]:
              newFormValues.includeMetadata,
          });
        } else {
          setSavedExport2dOptions({
            [EXPORT_FORM_FIELDS.FORMAT]: newFormValues.format,
            [EXPORT_FORM_FIELDS.REMOVE_BACKGROUND]:
              newFormValues.removeBackground,
            [EXPORT_FORM_FIELDS.INCLUDE_METADATA]:
              newFormValues.includeMetadata,
            [EXPORT_FORM_FIELDS.EXPORT_SIZE]: newFormValues.exportSize,
          });
        }
      } else if (
        prevFormValues?.saveSelections === true &&
        !newFormValues.saveSelections
      ) {
        if (exportModality === "video") {
          setSavedVideoOptions(undefined);
        } else {
          // Clear saved export 2d options explicitly when save selections is disabled
          setSavedExport2dOptions(undefined);
        }
      }
    },
  );

  // Update export dimensions when image dimensions change (e.g., different object selected)
  const handleImageDimensionsChanged = useEventCallback(
    (prevDimensionsValues: Dimensions, newDimensionsValues: Dimensions) => {
      const currentSize = form.getValues(EXPORT_FORM_FIELDS.EXPORT_SIZE) ?? 1;

      if (newDimensionsValues.width) {
        setValue(EXPORT_FORM_FIELDS.ORIGINAL_WIDTH, newDimensionsValues.width);
        setValue(
          EXPORT_FORM_FIELDS.EXPORT_WIDTH,
          Math.round(newDimensionsValues.width * currentSize),
        );
      }

      if (newDimensionsValues.height) {
        setValue(
          EXPORT_FORM_FIELDS.ORIGINAL_HEIGHT,
          newDimensionsValues.height,
        );
        setValue(
          EXPORT_FORM_FIELDS.EXPORT_HEIGHT,
          Math.round(newDimensionsValues.height * currentSize),
        );
      }

      if (newDimensionsValues.width && newDimensionsValues.height) {
        setValue(
          EXPORT_FORM_FIELDS.ORIGINAL_ASPECT_RATIO,
          newDimensionsValues.width / newDimensionsValues.height,
        );
      }
    },
  );

  useOnValueChanged(formValues, handleFormValuesChanged);
  useOnValueChanged(imageDimensions, handleImageDimensionsChanged);

  return (
    <Dialog
      data-testid="export-dialog"
      open={isOpen}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            height: "80vh",
            maxHeight: "800px",
          },
        },
      }}
    >
      <DialogHeader title={title} />
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 8,
          paddingX: 4,
          paddingY: 1.5,
          overflow: "hidden",
          flex: 1,
          position: "relative",
        }}
      >
        {shouldShowLoadingBanner && <ExportLoadingBanner />}
        <Box
          sx={{
            flex: "1.5 1 0",
            minWidth: 0,
            display: "flex",
          }}
        >
          <ExportPreview objects={objects} />
        </Box>
        <Box
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            pt: 3,
          }}
        >
          <FormProvider {...form}>
            <ExportForm
              exportModality={exportModality}
              isDisabled={isLoading}
            />
          </FormProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack gap={1}>
          <ExportFormUnitsContainer
            removeBackground={formValues.removeBackground}
            objects={objects}
          />
          <Stack direction="row" gap={1}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCancel}
              startIcon={<Icon icon={faXmark} />}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={!isValid}
              loading={isLoading}
              loadingPosition="end"
              onClick={handleSubmit(handleDownload)}
              startIcon={<Icon icon={faDownload} />}
              type="submit"
              variant="contained"
            >
              {downloadButtonText}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
